import { Suspense, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Sidebar from "./Sidebar";
import Header from "./Header";
import CommandPalette from "../components/CommandPalette";
import ToastContainer from "../components/ToastContainer";
import OnboardingModal from "../components/OnboardingModal";
import { routeLabel } from "../lib/navigation";
import { useRealtimeConnection } from "../realtime/hooks";
import { useRealtimeQueryBridge } from "../realtime/queryBridge";

function PageSkeleton() {
  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 animate-fade-in">
      <div className="h-8 w-48 skeleton rounded" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-28 skeleton rounded-card-lg" />
        ))}
      </div>
      <div className="h-72 skeleton rounded-card-lg" />
    </div>
  );
}

export default function AppLayout() {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  // Real-time connection + React Query bridge — mounted once here so every
  // page under this layout gets live updates without wiring anything itself.
  useRealtimeConnection();
  useRealtimeQueryBridge();

  // Close the mobile drawer, reset scroll, and sync the document title
  // whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
    mainRef.current?.scrollTo(0, 0);
    const label = routeLabel(location.pathname);
    document.title = label ? `${label} · Costorah` : "Costorah — AI Cost Intelligence";
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden bg-app-bg">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200]
                   focus:px-4 focus:py-2 focus:rounded-lg focus:bg-brand focus:text-app-bg
                   focus:text-sm focus:font-semibold focus:shadow-elevated"
      >
        Skip to main content
      </a>
      <CommandPalette />
      <ToastContainer />
      <OnboardingModal />
      <Sidebar mobileOpen={mobileNavOpen} onCloseMobile={() => setMobileNavOpen(false)} />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Header onMenuClick={() => setMobileNavOpen(true)} />
        <main ref={mainRef} id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="min-h-full"
          >
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
